import { AiConsentState } from '../attestations/ai-consent.types';
import { LatestAiAdvisory, LatestAiAttempt } from '../df/df.types';
import { CompanyLookup } from '../entity/entity.types';
import { KsAssessmentRecord } from '../kickstart/assessment-record.types';
import { KsFunderFlag } from '../kickstart/kickstart.types';
import { LookupListVersions } from '../kickstart/lookup-lists.types';

import { DocAssessStatus } from '../status/doc-assess.types';
import { StatusMeta } from '../status/status-meta.types';
import { SizingHold } from './sizing-hold.types';

/** The product-lane outcome the backend recommends (non-exclusive: BOTH is real). */
export type PreApplicationRecommendation =
  | 'KS_ONLY'
  | 'DF_ONLY'
  | 'BOTH'
  /** §A1.4: referred to a person at the door — a trust, an NPO, no CIPC registration. */
  | 'REFER'
  | 'DECLINE_TRADING'
  | 'DECLINE_JURISTIC'
  | 'NEEDS_MORE_INFO'
  | 'NONE';

/** Document buckets asked for during guided intake. */
export type PreApplicationDocType = 'BANK_STATEMENT' | 'FINANCIALS' | 'AGE_ANALYSIS' | 'OTHER';

/**
 * Client-facing names for the document buckets, matching the labels the intake
 * checklist puts above each drop zone. Ops screens render the raw enum; a client
 * screen never should.
 */
export const PRE_DOC_TYPE_LABELS: Record<PreApplicationDocType, string> = {
  BANK_STATEMENT: 'Bank statements',
  FINANCIALS: 'Financial statements',
  AGE_ANALYSIS: 'Age analysis',
  OTHER: 'Other supporting documents',
};

/** A document attached to a pre-application (same verdict shape as KS/DF docs). */
/**
 * What a bank statement says about itself (FC-DET-047): the account read off
 * its own face, masked to the tail, and its period. `source` names the reader
 * — the parser, or the per-document AI check as its fallback.
 */
export interface DetectedAccount {
  bank_name: string;
  product_name: string;
  account_number_tail: string;
  account_holder: string;
  source: 'PARSER' | 'DOC_CHECK' | '';
  period_start: string | null;
  period_end: string | null;
}

export interface PreApplicationDocument {
  short_id: string;
  doc_type: PreApplicationDocType;
  label: string;
  file_name: string;
  content_type: string;
  created_at: string;
  assess_status: DocAssessStatus;
  assess_message: string;
  /**
   * Which DECLARED bank account this statement came off (FLOW-243), or `null`
   * when nobody has attributed it. A `short_id` into the business's declared
   * account list — the list is the thing that identifies the account, and a
   * copy carried on every document would be a second place for it to go stale.
   */
  source_account_ref: string | null;
  /** The account on the statement's own face, or `null` until something read it. */
  detected_account: DetectedAccount | null;
}

/** The manual-review lifecycle of a pre-application (FLOW-148). */
export type PreApplicationReviewStatus =
  'OPEN' | 'SUBMITTED' | 'MORE_INFO_REQUIRED' | 'PROCEEDED' | 'DECLINED';

/** Chip labels for the review lifecycle — consumed by `<app-status-chip>`. */
export const PRE_REVIEW_STATUS_META: Record<PreApplicationReviewStatus, StatusMeta> = {
  // Amber where someone has to act; neutral while it simply waits.
  OPEN: { label: 'Open', variant: 'neutral' },
  SUBMITTED: { label: 'Submitted — under review', variant: 'default' },
  MORE_INFO_REQUIRED: { label: 'More information required', variant: 'warning' },
  PROCEEDED: { label: 'Proceeded', variant: 'success' },
  DECLINED: { label: 'Declined', variant: 'destructive' },
};

/**
 * Presentation codes for the enquiry's standing PER PRODUCT — the confirmation's
 * status card and the dashboard's Debtors Finance chip.
 *
 * ⚠️ NOT lifecycle statuses. `PreApplicationReviewStatus` above is the only
 * lifecycle a pre-application has, and a Debtors Finance application exists
 * only once Ops proceeds to products. These describe the ONE enquiry from each
 * product's side (`df_intent` is the client's recorded interest, nothing more)
 * and never appear in an API payload. Kept beside the review-status meta so the
 * shared status chip can render them with a variant, not a bare string.
 */
export const PRE_ENQUIRY_PRODUCT_META: Record<
  'KS_UNDER_REVIEW' | 'DF_INTEREST_RECORDED' | 'DF_NOT_REQUESTED' | 'DF_INTEREST',
  StatusMeta
> = {
  KS_UNDER_REVIEW: { label: 'Under review', variant: 'default' },
  DF_INTEREST_RECORDED: {
    label: 'Interest recorded — reviewed with your enquiry',
    variant: 'default',
  },
  DF_NOT_REQUESTED: {
    label: 'Not requested — share an age analysis to be considered',
    variant: 'neutral',
  },
  // The dashboard's second, smaller chip on an enquiry row.
  DF_INTEREST: { label: 'Debtors Finance interest', variant: 'default' },
};

/**
 * The client-safe pre-application record.
 *
 * N4: the internal routing enum (`recommendation`), the raw `signals`, and the
 * Ops-only decline note are NOT on this shape — the backend's client serializer
 * doesn't send them, and a guard test fails if one ever reappears. The client's
 * "why" is the plain-language `client_decision_reason` plus the outcome copy from
 * `PreApplicationRecommendationResult`.
 */
export interface PreApplication {
  short_id: string;
  entity_short_id: string;
  entity_name: string;
  /** Who at CapX is dealing with it; '' when nobody is (Henk, 2026-10-23). */
  dealt_with_by: string;
  df_intent: boolean;
  review_status: PreApplicationReviewStatus;
  client_decision_reason: string;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
  documents: PreApplicationDocument[];
  /**
   * Whether an un-revoked POPIA consent to AI-assisted assessment stands for
   * this intake. Derived server-side from the append-only consent rows — the
   * intake page reads it so someone continuing a consented intake isn't asked
   * again, and uploads stay blocked until it is `true`.
   */
  ai_processing_consent: boolean;
  /**
   * FLOW-290 — the same consent as `ai_processing_consent`, but as state plus
   * timestamps so the upload step can offer the withdrawal it promises and say
   * when it was taken. Both derive from the same rows, so they cannot disagree.
   */
  ai_consent: AiConsentState | null;
  /**
   * Whether the client may still ADD a document. Every editable state, plus
   * SUBMITTED (PO ruling 2026-09-22: a submitted enquiry accepts uploads,
   * optionally, add-only). PROCEEDED is closed.
   */
  accepts_documents: boolean;
  /** Documents added while the enquiry was already under review. */
  post_submission_document_count: number;
}

/** The N4-safe recommendation outcome shown to the client. */
export interface PreApplicationRecommendationResult {
  recommendation: PreApplicationRecommendation;
  headline: string;
  reason: string;
  declined: boolean;
  /**
   * §A1.4: referred to a person at the door. The OUTCOME only — the reason
   * (§A13.2's `referral_reason`) is Ops's and never reaches a client payload.
   */
  referred: boolean;
  /** Whether the KS CTA may be offered (REFER is not self-serve). */
  ks_available: boolean;
  df_available: boolean;
}

export type ProceedProduct = 'KS' | 'DF';

// ---------------------------------------------------------------------------
// Ops worklist (FLOW-149) — these shapes come from the Ops-only endpoints and
// carry the internal reasoning. Never render them on a client-facing screen.
// ---------------------------------------------------------------------------

/**
 * The >90-day document-age advisory (ruling Q6).
 *
 * Reviewer-facing and ADVISORY — it is deliberately not a gate: nothing in the
 * decision flow reads `stale` to enable or disable an action.
 */
interface DocumentAgeAdvisory {
  stale: boolean;
  threshold_days: number;
  oldest_document_at: string | null;
  oldest_document_age_days: number | null;
  /** What the banner leads with. Empty unless `stale`. */
  headline: string;
  message: string;
}

/** One row of the append-only audit trail (N8). */
export interface PreApplicationEvent {
  short_id: string;
  created_at: string;
  note: string;
  recommendation: PreApplicationRecommendation;
  signals: Record<string, unknown>;
  actor_name: string;
  actor_email: string | null;
}

/** A row in the Ops worklist — enough to triage without opening the record. */
export interface PreApplicationOpsRow {
  short_id: string;
  entity_short_id: string;
  entity_name: string;
  agency_name: string | null;
  submitted_by: string;
  assignee_name: string;
  review_status: PreApplicationReviewStatus;
  recommendation: PreApplicationRecommendation;
  df_intent: boolean;
  document_count: number;
  documents_stale: boolean;
  /** Documents the client added while the enquiry was already under review. */
  post_submission_document_count: number;
  submitted_at: string | null;
  created_at: string;
}

/**
 * The latest indicative Kickstart figure for a pre-application (FLOW-224 part 1).
 *
 * 🚨 **Ops-internal — never render this on a client-facing screen.** Two rules bar
 * it and only one of them ever lifts: §A12's publication rule bars any figure from
 * a client while the reproducibility gate is recorded as failed, and N4 keeps the
 * engine's ceiling internal regardless — the client is told the outcome, never the
 * arithmetic. `indicative_sizing` is registered in the backend's N4 field registry
 * and a guard test fails if a client serializer ever names it.
 *
 * The run belongs to the Kickstart DRAFT the figure was computed against, not to
 * the pre-application: the rules engine never reads a pre-application record.
 */
/**
 * One published Kickstart figure, as §A12 P2 requires it: a set of LABELLED
 * EVIDENCE STATES, never an unlabelled band. `amount` is null on
 * BELOW_FLOOR / ABOVE_CEILING — §A12 P3 withholds the number itself when it
 * falls outside what CapX lends, and null here is that withholding, never a
 * zero.
 */
interface PublishedIndicativeFigure {
  evidence_state: string;
  state: 'FIGURE' | 'BELOW_FLOOR' | 'ABOVE_CEILING';
  amount: string | null;
}

/**
 * The Debtors Finance half of the pre-assessment: a qualification reading and
 * **no amount**.
 *
 * 🚨 There is no `amount` key and there must never be one. §A12.4 leaves the
 * Debtors Finance figure undetermined — its base is set per facility by a
 * named Credit Committee decision — and UI-DA-58-01 bars a surface from even
 * reserving space for a figure that has not been computed.
 */
interface DebtorsFinanceReading {
  state: 'QUALIFIES' | 'NEEDS_AGE_ANALYSIS' | 'NOT_ELIGIBLE' | 'NOT_ASSESSED';
  reading: string;
  age_analysis_on_file: boolean;
  /** The client's own recorded answer (df_intent) — an answer, not a finding. */
  client_asked_for_it: boolean;
  limit_not_computed_because: string;
  limit_inputs_outstanding: string[];
}

/**
 * The pre-assessment (Constitution §B4.1c, FC-DET-048): the provisional
 * reading shown after the statements are analysed and before Ops has engaged
 * the client. Computed as if no funder had been declared; no offer is made on
 * it. The Kickstart half is whatever §A12 permits — `null` while the
 * publication gate is shut.
 */
interface PreAssessment {
  provisional: boolean;
  basis: string;
  kickstart: { figure: PublishedIndicativeFigure[] | null };
  debtors_finance: DebtorsFinanceReading;
}

export interface IndicativeSizing {
  /**
   * A run has been asked for and has not produced a result yet. The assessment
   * is queued off the request path (a real one takes ~165s against a 120s
   * gunicorn ceiling), so the POST returns before there is anything to show.
   * When this is true and no run has completed, every other field is absent.
   */
  in_flight: boolean;
  run_short_id?: string;
  /** The Kickstart draft the run was computed against. */
  draft_short_id?: string | null;
  created_at?: string;
  /** False = the chain could not be closed from these inputs. */
  determinable?: boolean;
  /** False = it computed, and came in under the product floor. */
  viable?: boolean;
  /** Which of the two "no" answers this is, in the scorer's own words. */
  reason?: string;
  /**
   * Decimal string, or `null` on a refused or below-floor run — never `'0'`.
   * A null here is "no recommendation", not "we will lend nothing".
   */
  amount?: string | null;
  flags?: string[];
  scorer_version?: string;
  /** Which model answered. The admin list overrides the settings default. */
  model?: string;
}

/** The reviewer's full view of one submitted pre-application. */
/**
 * The journey flowchart on the Ops detail screen (`applications/journey.py`).
 * A read-only projection: every state is read off a field or event the owning
 * service wrote. Recovery is a `warning` tone on "Repaying", never a step (§B12).
 */
type JourneyStepState = 'done' | 'current' | 'upcoming' | 'branch';
type JourneyStepTone = 'neutral' | 'positive' | 'warning' | 'negative';

export interface JourneyStep {
  key: string;
  /** Which column: the business and those acting for it, an automatic act, CapX's people. */
  lane: 'client' | 'system' | 'capx';
  title: string;
  /** The actor the rule names, in its words — finer than the lane. */
  who: string;
  /** The provision(s) the step rests on, or why the rulebook has no such step. */
  rule: string;
  detail: string;
  state: JourneyStepState;
  tone: JourneyStepTone;
  at: string | null;
  actor: string;
  /** Who this step emails; empty when it sends nothing. */
  emails: ('client' | 'capx')[];
}

interface JourneyPhase {
  key: string;
  title: string;
  steps: JourneyStep[];
}

export interface PreApplicationJourney {
  phases: JourneyPhase[];
  /** The step(s) the record is waiting at — several when more than one act is open. */
  current: string[];
  /** When the "within one business day" review promise falls due. */
  review_due_at: string | null;
  review_overdue: boolean;
}

export interface PreApplicationOps {
  short_id: string;
  entity_short_id: string;
  entity_name: string;
  agency_name: string | null;
  submitted_by: string;
  assignee_name: string;
  df_intent: boolean;
  review_status: PreApplicationReviewStatus;
  /** Internal — Ops only (N4). */
  recommendation: PreApplicationRecommendation;
  recommended_application_path: 'WITH_FINANCIALS' | 'BANK_ONLY' | null;
  signals: Record<string, unknown>;
  /**
   * §A13.2's referral trigger as the door wrote it (`ENTITY_FORM`), its label,
   * and what the referral must say beyond its reason (§A1.4.3 — on a trust,
   * escalate rather than decide). `null` / `''` when the door did not refer.
   * Ops-only (N4); the screen renders it and branches on nothing in it.
   */
  referral_reason: string | null;
  referral_reason_display: string;
  referral_note: string;
  /**
   * FLOW-290 — AI-processing consent state, so the screen can offer the
   * withdrawal it promises. `null` = never asked (not the same as withdrawn).
   */
  ai_consent: AiConsentState | null;

  ops_decision_note: string;
  client_decision_reason: string;
  decided_by_name: string;
  decided_at: string | null;
  document_age: DocumentAgeAdvisory;
  documents: PreApplicationDocument[];
  events: PreApplicationEvent[];
  /** Documents the client added while the enquiry was already under review, and when the latest landed. */
  post_submission_document_count: number;
  post_submission_documents_at: string | null;
  resulting_ks: string | null;
  resulting_df: string | null;
  /** Ops-internal (N4 + §A12). `null` until a figure has been asked for. */
  indicative_sizing: IndicativeSizing | null;
  /** What the AI said about this intake, and its latest attempt. Ops-only (N4). */
  ai_assessment: {
    advisory: LatestAiAdvisory | null;
    attempt: LatestAiAttempt | null;
    /** The run in flight, for someone who joins partway; `null` when none is. */
    run: { requested_at: string; requested_by: string } | null;
    /** *Where the assessment stands*, from the draft it ran on; `null` with no draft. */
    record: KsAssessmentRecord | null;
  };
  /** The pre-assessment as CapX staff read it. Ops-only (N4). */
  pre_assessment_output: PreAssessmentOutput;
  /** The pre-assessment — both products, one shape (FC-DET-048). Ops-only. */
  pre_assessment: PreAssessment;
  /**
   * The indicative Kickstart draft's short_id, or `null` when none exists.
   *
   * The hold below is recorded against the DRAFT, so releasing it posts to
   * Kickstart's endpoint keyed on this id — not to anything on this intake.
   * Its own field rather than read off `indicative_sizing`, which is `null` on
   * exactly the files that are held (FLOW-283).
   */
  indicative_ks: string | null;
  /**
   * The open declared-account hold on that draft (FLOW-277), or `null`.
   *
   * 🚨 **Read this BEFORE `indicative_sizing`.** A held intake has no
   * `SizingRun` at all, so `indicative_sizing` answers `null` for it exactly as
   * it does for an intake nobody has asked about — which is what left the panel
   * offering the request button as though nothing had happened (FLOW-283).
   */
  sizing_hold: SizingHold | null;
  /**
   * Whether the client has affirmed its account list is COMPLETE (FLOW-284).
   *
   * 🔑 **Read beside `sizing_hold`: it decides whether the release ACTION is
   * offered.** Limb (a) is re-read on every release, so releasing a
   * `DECLARED_ACCOUNTS_NOT_ON_FILE` hold while this is `false` raises an
   * identical hold seconds later — and the API now refuses it with a 409.
   * A fact about the ENTITY, so it is `false` on a file with no hold too.
   */
  affirmation_in_force: boolean;
  /** The flowchart: intake to a repaid facility, read off this record. */
  journey: PreApplicationJourney;
  /**
   * FLOW-402 (DOC-89-A §4) — each folded card's one line, keyed by the card,
   * in the record's words (`applications/panel_lines.py`).
   */
  panel_lines: Record<string, string>;
  /**
   * FLOW-397 (`FC-DET-115`, DOC-91 §5) — *Company registration — capture
   * first*. The record's state and which of Proceed / Decline the server
   * refuses, with the line to show in its place. The screen decides nothing.
   */
  company_registration: CompanyRegistration;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

/** The CIPC registration date and status as Ops's first act on the file. */
export interface CompanyRegistration {
  /** The registration date is on file (one record with *Business verification*). */
  captured: boolean;
  registration_date: string | null;
  cipc_status: string | null;
  cipc_status_display: string | null;
  captured_by: string;
  captured_at: string | null;
  file_id: number | null;
  file_name: string;
  /** Given by the business at registration — information only, nothing worked out. */
  reg_no_given: string;
  entity_type_display: string;
  /** The business's own trading-start (DOC-91 §2A) — information only. */
  trading_start_date: string | null;
  /** CIPC's answer is the capture (DOC-93 §3); Ops captures where it gave nothing. */
  from_cipc: boolean;
  lookup_on: boolean;
  lookup_state: '' | 'FOUND' | 'NOT_FOUND' | 'ERROR';
  looked_up_at: string | null;
  company_lookup: CompanyLookup | null;
  lookup_differs: string[];
  deregistered_at_door: boolean;
  resigned_directors: { declaration: string; full_name: string; resigned_on: string | null }[];
  status_correction: { cipc_status: string; by: string; at: string } | null;
  /** The number Ops typed off the CIPC document, and whether it differs. */
  reg_no_as_printed: string;
  reg_no_differs: boolean;
  /** *No CIPC record found* — the front-door referral (§A1.4); stays once a date is captured. */
  no_record: {
    reason: string;
    recorded_by: string;
    recorded_at: string;
    limb: string;
    disposition: { kind: 'DECLINED' | 'REGISTRATION_CAPTURED'; by: string; at: string } | null;
  } | null;
  /** DOC-91's line where the server refuses the act; `null` where it is offered. */
  proceed_refused: string | null;
  decline_refused: string | null;
  /** The one re-run on the capture, as recorded. */
  rerun: { by: string; at: string; in_flight: boolean } | null;
}

/** The capture (multipart): the date, the status, and the document — an upload with its source, or an entry already in the block. */
export interface CompanyRegistrationPayload {
  registration_date: string;
  cipc_status: string;
  file?: File;
  source?: 'BUSINESS_RECEIVED' | 'CAPX_OWN';
  obtained_from?: string;
  received_how?: string;
  received_at?: string;
  entry?: string;
  reg_no_as_printed?: string;
}

export interface PreApplicationOpsListResponse {
  pre_applications: PreApplicationOpsRow[];
}

/** Human labels for the internal routing enum — Ops screens only. */
export const PRE_RECOMMENDATION_LABELS: Record<PreApplicationRecommendation, string> = {
  KS_ONLY: 'Kickstart Cash',
  DF_ONLY: 'Debtors Finance',
  BOTH: 'Both',
  REFER: 'Referred — a CapX person will be in touch',
  DECLINE_TRADING: 'Declined — trading history',
  DECLINE_JURISTIC: 'Declined — not a registered company',
  NEEDS_MORE_INFO: 'Needs more information',
  NONE: 'Not yet evaluated',
};

/**
 * The Ops decision payload. Decline carries BOTH reason fields (ruling Q3).
 * *Request more information* is withdrawn (FLOW-381, DOC-89): Ops asks by email.
 */
export type OpsDecisionAction = 'proceed' | 'decline';

export interface OpsDecisionPayload {
  action: OpsDecisionAction;
  /** proceed: omit to open the product(s) the recommendation implies. */
  products?: ProceedProduct[];
  /** decline: the Ops-only documented reason. */
  ops_note?: string;
  /** decline: the plain-language reason the client sees. */
  client_reason?: string;
}

/** Ops-only (N4). Composed by `applications/pre_assessment.ops_output`. */
interface PreAssessmentRun {
  trigger: 'SUBMISSION' | 'DATE_CAPTURE' | 'OPS_RERUN';
  started_by: string;
  requested_at: string;
  status: string;
  model: string;
  run_short_id: string | null;
  behind_client_figure: boolean;
  documents_read: string[];
}

interface PreAssessmentSizing {
  base_safe_exposure: string | null;
  liquidity_anchor: string | null;
  internal_reduction: string | null;
  reduction_log: { factor_id: string; label: string; rate: string; value_after: string }[];
  post_reduction_exposure: string | null;
  affordability_cap: string | null;
  bank_only_cap: string | null;
  bank_only_path_fixed: boolean;
  confidence_score: string | null;
  confidence_band: string | null;
  uplift_eligible: boolean;
  uplift_amount: string;
  system_recommended_amount: string | null;
  pricing_tier: string | null;
  volatility_coefficient: string | number | null;
  funders_detected: number | null;
  funders_undisclosed: number | null;
  bureau_score: number | null;
  repayment_options: { frequency: string; instalments: number; instalment_amount: string }[];
  mandatory_flags: string[];
  gaps: string[];
}

export interface AiProductRecommendation {
  recommendation: 'GO_FORWARD' | 'DO_NOT_GO_FORWARD' | 'CANNOT_SAY';
  reason: string;
}

/** Where the file could go, and what to know first. An indication: it gates nothing. */
export interface PreAssessmentDirection {
  /** `null` where the intake has no draft to read. */
  scorecard: { kickstart: ProductScorecard; debtors_finance: ProductScorecard } | null;
  /** The model's own view per product. Null on an earlier run or a stub. */
  ai_recommendation: {
    kickstart: AiProductRecommendation;
    debtors_finance: AiProductRecommendation;
  } | null;
  kickstart: {
    state:
      | 'CAN_PROCEED'
      | 'WAITING_ON_DATE'
      | 'ON_HOLD'
      | 'WITH_A_PERSON'
      | 'NOT_READ'
      | 'STATEMENTS_OUT_OF_DATE';
    reason: string;
  };
  debtors_finance: {
    state: 'WORTH_ASKING' | 'NO_SIGN' | 'NOT_READ' | 'NOT_OFFERED';
    reason: string;
    /** Read by the model off the statements. */
    evidence: string;
    needs: string[];
  };
  issues: { text: string; blocks: boolean; read_by_ai: boolean; detail: string[] }[];
  /** `null` where the intake has no draft to read. */
  conduct: StatementConduct | null;
  /** `null` where this run read no series. */
  unclassified: UnclassifiedReceipts | null;
}

/** A statement line, as the statement prints it. */
export interface StatementLine {
  date: string;
  amount: string;
  description: string;
}

/** One limb of the model's transaction behaviour. A flag, not a finding (§B2a). */
export interface ConductFlag {
  limb: string;
  label: string;
  status: 'observed' | 'unresolvable';
  period: string;
  cadence: string;
  reason: string;
  evidence: StatementLine[];
}

/** Counted by code from the statement lines (§B3.3.2), with the run's flags beside it. */
export interface StatementConduct {
  state: 'CLEAN' | 'RISING' | 'NOT_CLEAN' | 'TOO_SHORT' | 'NOT_READ';
  headline: string;
  reading: string;
  overdraft_line: string;
  short_note: string;
  returned_debits: number | null;
  lines: StatementLine[];
  ai_disagrees: boolean;
  /** `null` on a run from before the model reported these. */
  ai_flags: ConductFlag[] | null;
}

/** §2A.2: receipts whose character nobody could establish. They stay in turnover. */
export interface UnclassifiedReceipts {
  count: number;
  total: string;
  turnover: string;
  share_pct: number | null;
  largest: StatementLine[];
  more: number;
}

/** One model's run of the assessment, read as the file would read it. Ops-only (N4). */
export interface ModelRun {
  short_id: string;
  model: string;
  provider_label: string;
  status: 'RUNNING' | 'COMPLETE' | 'FAILED';
  error: string;
  created_at: string;
  cost_zar: string;
  latency_ms: number;
  input_tokens: number;
  output_tokens: number;
  /** The run the file stands on. Any other tab is a comparison and changes nothing. */
  is_current: boolean;
  summary: string;
  missing_items: string[];
  outcome: PreApplicationOps['pre_assessment_output']['outcome'] | null;
  direction: PreAssessmentDirection | null;
  /** A run answered by one call per area: each area's call. Empty for a one-call run. */
  area_calls?: {
    area: string;
    model: string;
    status: 'RUNNING' | 'COMPLETE' | 'FAILED';
    error: string;
    cost_zar: string;
    latency_ms: number;
    input_tokens: number;
    output_tokens: number;
  }[];
}

export interface ModelRuns {
  baseline: string | null;
  models: { model: string; thinking: boolean; provider_label: string }[];
  runs: ModelRun[];
}

/** One line of a product's scorecard. OPEN: the record cannot answer it yet. */
export interface ScorecardLine {
  key: string;
  label: string;
  points: number;
  of: number;
  state: 'MET' | 'PART' | 'NOT_MET' | 'OPEN';
  evidence: string;
}

/** How strong the submission looks for one product. Code's sum; gates nothing. */
export interface ProductScorecard {
  state: 'SCORED' | 'NOT_READ' | 'NOT_SCORED';
  percent: number | null;
  strength: string;
  /** The points on lines the record can answer. The score is out of these. */
  decided_points: number;
  open_points: number;
  could_reach: number | null;
  cap_reason: string;
  reason: string;
  lines: ScorecardLine[];
}

export interface PreAssessmentOutput {
  provisional_basis: string;
  rerun_refused: { code: string; reason: string } | null;
  /** While testing, a current result may be run again on the same material. */
  repeat_runs?: boolean;
  history: PreAssessmentRun[];
  out_of_date: { as_at: string; material: { at: string; label: string }[] } | null;
  outcome: {
    kind: 'FIGURE' | 'HELD' | 'REFERRED' | 'NO_FIGURE';
    reason: string;
    amount: string | null;
  };
  direction: PreAssessmentDirection;
  sizing: PreAssessmentSizing | null;
  act_on: {
    funder_question: {
      status: string;
      payer_to_ask_about: { name: string; share_pct: string | number | null } | null;
      client_words: string[];
    };
    flagged_receipts: unknown;
    accounts: {
      derived: { account: string; bank: string; holder: string }[];
      principal_account: string | null;
      named_not_uploaded: { bank: string; last_four: string; reason: string }[];
      movement_over_threshold: unknown;
    };
    hold: { short_id: string; reason: string; rule: string } | null;
    funder_investigation: {
      detections: { name?: string; counterparty?: string; evidence_basis?: string }[] | null;
      flags: KsFunderFlag[];
      lookup_list_versions: Partial<LookupListVersions> | null;
    };
    statement_age: {
      latest_period_end: string;
      age_days: number;
      stale: boolean;
      read_by_ai: boolean;
      message: string;
    } | null;
    registration: { captured: boolean; no_record: boolean };
    tax_pin_can_be_asked: boolean;
    debtors_finance: { state: string; reading: string };
  } | null;
  missing_inputs: { item: string; supplied_by: string }[];
  /** The run area by area: what the model read beside what the engine did. */
  areas?: AssessmentAreaPanel[];
}

/** Who answered one area, and what it cost. Mirrors `assessment_areas._provenance`. */
export interface AreaProvenance {
  model: string;
  provider_label: string;
  prompt_label: string;
  prompt_sha256: string;
  status: 'RUNNING' | 'COMPLETE' | 'FAILED';
  error: string;
  /** The area had its own call (`§A11.7` U7); false: the run's one call answered it. */
  own_call: boolean;
  /** Held from an earlier run by a re-run of another area (plan Phase E). */
  held: boolean;
  /** The AI call log row that answered this area. */
  log_ref: string;
  cost_zar: string;
  latency_ms: number;
  input_tokens: number;
  output_tokens: number;
  is_stub: boolean;
}

/** One panel of the pre-assessment, Ops-only (N4). Mirrors `assessment_areas.areas`. */
export interface AssessmentAreaPanel {
  key: 'receipts' | 'funders' | 'conduct' | 'counterparties' | 'file';
  title: string;
  purpose_line: string;
  rule_cites: string;
  status: 'complete' | 'failed' | 'not_run';
  failure: string;
  provenance: AreaProvenance | null;
  /** The model's reading; its shape is the area's (see the backend builders). */
  model_read: Record<string, unknown>;
  /** What the engine did with it, in plain lines. Code's, never the model's. */
  system_did: string[];
  /** The area's grade: the engine's disposition, its figures, and how much the model resolved. */
  grade: AreaGrade;
}

/**
 * One area's grade. Not points: the rulebook weights nothing at the pre-assessment, so
 * the verdict is the disposition the engine reached under the area's rule. Mirrors
 * `assessment_areas._grade`.
 */
export interface AreaGrade {
  verdict: string;
  tone: 'success' | 'warning' | 'destructive' | 'neutral';
  measures: { label: string; value: string; rule: string }[];
  /** Parts of the area the model answered, observed or none observed (`§A11` U1/U2). */
  resolved: { read: number; of: number };
}

/** Mirrors `indicative_sizing.run_options`. */
export interface AssessmentRunOptions {
  models: { model: string; recommended: boolean; already_run: boolean }[];
  runs_left: number;
}
